import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Checkbox } from "@babackapp/ui/components/checkbox";
import { fetchClient } from "@babackapp/private-api-client/generated/client";
import useTranslation from "@babackapp/translations/use-translation";
import type { ShopDto } from "@babackapp/private-api-client/types";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const FEDEX_EULA_URL = "https://baback-public-assets.s3.eu-west-1.amazonaws.com/legal/fedex-eula.pdf";

export type MfaOptions = {
  accountAuthToken: string;
  isMfaRequired: boolean;
  email?: string;
  phoneNumber?: string;
  options?: {
    invoice?: string;
    secureCode?: string[];
  };
};

export type CspStep = "account" | "eula" | "mfa" | "pin" | "invoice" | "done";

function extractApiMessage(m: unknown): string | undefined {
  if (typeof m === "string") return m;
  if (Array.isArray(m)) {
    const parts = m
      .map((x) =>
        typeof x === "string"
          ? x
          : x && typeof x === "object" && "message" in x
            ? String((x as { message: unknown }).message)
            : "",
      )
      .filter(Boolean);
    return parts.length ? parts.join(", ") : undefined;
  }
  return undefined;
}

export function FedexCspRegistrationForm({
  shopId,
  shop,
  defaultAccountNumber,
  onRegistrationComplete,
  initialStep,
  initialMfaOptions,
}: {
  shopId: string;
  shop: ShopDto;
  defaultAccountNumber?: string;
  onRegistrationComplete: () => void;
  initialStep?: CspStep;
  initialMfaOptions?: MfaOptions;
}) {
  const t = useTranslation();
  const billing = shop.billingAddress;

  const [step, setStep] = useState<CspStep>(initialStep ?? "account");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPassthrough, setIsPassthrough] = useState(false);

  const [accountNumber, setAccountNumber] = useState(defaultAccountNumber || "");
  const [customerName, setCustomerName] = useState(
    billing?.companyName || shop.name || "",
  );
  const [streetLine1, setStreetLine1] = useState(billing?.address1 || "");
  const [streetLine2, setStreetLine2] = useState(billing?.address2 || "");
  const [city, setCity] = useState(billing?.city || "");
  const [stateOrProvinceCode, setStateOrProvinceCode] = useState(
    billing?.province_code || "",
  );
  const [postalCode, setPostalCode] = useState(billing?.zip || "");
  const [countryCode, setCountryCode] = useState(
    billing?.country_code || "US",
  );

  const [mfaOptions, setMfaOptions] = useState<MfaOptions | null>(
    initialMfaOptions ?? null,
  );
  const [pin, setPin] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceCurrency, setInvoiceCurrency] = useState("USD");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("");

  const [eulaScrolled, setEulaScrolled] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [eulaWidth, setEulaWidth] = useState(0);
  const eulaContainerRef = useRef<HTMLDivElement>(null);

  const handleEulaScroll = useCallback(() => {
    const el = eulaContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    if (atBottom) setEulaScrolled(true);
  }, []);

  // size the rendered PDF to the container so it never adds a horizontal scrollbar
  useEffect(() => {
    const el = eulaContainerRef.current;
    if (step !== "eula" || !el) return;
    const update = () => setEulaWidth(el.clientWidth - 16);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [step]);

  const apiFetch = async (path: string, body: unknown): Promise<Record<string, unknown>> => {
    const { data, error, response } = await fetchClient.POST(
      path as never,
      { body } as never,
    );
    if (error || !response.ok) {
      const err = error as
        | { message?: unknown; errors?: { message?: string }[] }
        | undefined;
      const msg = extractApiMessage(err?.message) ?? err?.errors?.[0]?.message;
      throw new Error(msg || `HTTP ${response.status}`);
    }
    return data as Record<string, unknown>;
  };

  const handleProceedToEula = () => {
    setStep("eula");
  };

  const handleEulaAcceptAndRegister = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiFetch(
        `/shops/${shopId}/carriers/fedex/register`,
        {
          accountNumber,
          customerName,
          address: {
            streetLine1,
            streetLine2: streetLine2 || undefined,
            city,
            stateOrProvinceCode: stateOrProvinceCode || undefined,
            postalCode,
            countryCode,
          },
        },
      );

      if (data.isRegistered) {
        setIsPassthrough(Boolean(data.isPassthrough));
        setStep("done");
      } else if (data.mfaOptions) {
        setMfaOptions(data.mfaOptions as MfaOptions);
        setStep("mfa");
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestPin = async (option: "SMS" | "EMAIL" | "CALL") => {
    if (!mfaOptions) return;
    setIsLoading(true);
    setError(null);
    try {
      await apiFetch(
        `/shops/${shopId}/carriers/fedex/register/request-pin`,
        {
          accountAuthToken: mfaOptions.accountAuthToken,
          option,
        },
      );
      setStep("pin");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidatePin = async () => {
    if (!mfaOptions) return;
    setIsLoading(true);
    setError(null);
    try {
      await apiFetch(
        `/shops/${shopId}/carriers/fedex/register/validate-pin`,
        {
          accountAuthToken: mfaOptions.accountAuthToken,
          pin,
          accountNumber,
        },
      );

      setStep("done");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidateInvoice = async () => {
    if (!mfaOptions) return;
    setIsLoading(true);
    setError(null);
    try {
      await apiFetch(
        `/shops/${shopId}/carriers/fedex/register/validate-invoice`,
        {
          accountAuthToken: mfaOptions.accountAuthToken,
          invoiceDetail: {
            number: Number(invoiceNumber),
            currency: invoiceCurrency,
            date: invoiceDate,
            amount: Number(invoiceAmount),
          },
          accountNumber,
        },
      );
      setStep("done");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  if (step === "done") {
    return (
      <div className="text-center py-4 space-y-3">
        <img src="/fedex.png" alt="FedEx" className="h-8 mx-auto mb-2 p-2 box-content" />
        <p className="text-green-600 font-medium">
          {t("carrier.fedexCsp.success")}
        </p>
        {isPassthrough && (
          <p className="text-sm text-gray-500">
            {t("carrier.fedexCsp.passthroughSuccess")}
            {accountNumber ? ` (${accountNumber})` : ""}
          </p>
        )}
        <div className="flex justify-center gap-2">
          <Button variant="default" size="lg" onClick={onRegistrationComplete}>
            {t("carrier.fedexCsp.close")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 overflow-hidden">
      <img src="/fedex.png" alt="FedEx" className="h-8 mb-1 p-2 box-content" />
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}
      {error && (step === "mfa" || step === "pin" || step === "invoice") && (
        <p className="text-sm text-gray-500 border-t pt-3">
          {t("carrier.fedexCsp.techSupportFallback")}
        </p>
      )}

      {step === "account" && (
        <>
          <p className="text-sm text-gray-500">
            {t("carrier.fedexCsp.registerDescription")}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("carrier.fedexCsp.accountNumber")}
            </label>
            <Input
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder={t("carrier.fedexCsp.accountNumberPlaceholder")}
              className="mt-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("carrier.fedexCsp.companyName")}
            </label>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder={t("carrier.fedexCsp.companyNamePlaceholder")}
              className="mt-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("carrier.fedexCsp.streetAddress")}
            </label>
            <Input
              value={streetLine1}
              onChange={(e) => setStreetLine1(e.target.value)}
              placeholder={t("carrier.fedexCsp.streetLine1Placeholder")}
              className="mt-1"
            />
            <Input
              value={streetLine2}
              onChange={(e) => setStreetLine2(e.target.value)}
              placeholder={t("carrier.fedexCsp.streetLine2Placeholder")}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.city")}
              </label>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.stateProvince")}
              </label>
              <Input
                value={stateOrProvinceCode}
                onChange={(e) => setStateOrProvinceCode(e.target.value)}
                placeholder={t("carrier.fedexCsp.stateProvincePlaceholder")}
                className="mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.postalCode")}
              </label>
              <Input
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.countryCode")}
              </label>
              <Input
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                placeholder="US"
                className="mt-1"
              />
            </div>
          </div>
          <Button
            variant="default"
            size="lg"
            disabled={
              !accountNumber ||
              !customerName ||
              !streetLine1 ||
              !city ||
              !postalCode ||
              !countryCode
            }
            onClick={handleProceedToEula}
          >
            {t("carrier.fedexCsp.registerButton")}
          </Button>
        </>
      )}

      {step === "eula" && (
        <>
          <p className="text-sm font-medium text-gray-700">
            {t("carrier.fedexCsp.eulaTitle")}
          </p>
          <p className="text-sm text-gray-500">
            {t("carrier.fedexCsp.eulaDescription")}
          </p>
          <div
            ref={eulaContainerRef}
            onScroll={handleEulaScroll}
            className="max-h-[500px] overflow-y-auto overflow-x-hidden border border-gray-200 rounded bg-gray-50 p-2"
          >
            <Document
              file={FEDEX_EULA_URL}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={<p className="text-sm text-gray-400 p-4">Loading EULA...</p>}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i + 1}
                  pageNumber={i + 1}
                  width={eulaWidth || undefined}
                />
              ))}
            </Document>
          </div>
          {!eulaScrolled && (
            <p className="text-xs text-amber-600">
              {t("carrier.fedexCsp.eulaScrollPrompt")}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Checkbox
              id="fedex-eula-accept"
              checked={eulaAccepted}
              onCheckedChange={(checked) =>
                setEulaAccepted(checked === true)
              }
              disabled={!eulaScrolled}
            />
            <label
              htmlFor="fedex-eula-accept"
              className="text-sm text-gray-700 cursor-pointer"
            >
              {t("carrier.fedexCsp.eulaCheckbox")}
            </label>
          </div>
          <div className="flex gap-2">
            <Button
              variant="default"
              size="lg"
              disabled={!eulaAccepted || !eulaScrolled || isLoading}
              onClick={handleEulaAcceptAndRegister}
            >
              {isLoading
                ? t("carrier.fedexCsp.registering")
                : t("carrier.fedexCsp.eulaAccept")}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => {
                setStep("account");
                setEulaScrolled(false);
                setEulaAccepted(false);
              }}
            >
              {t("carrier.fedexCsp.back")}
            </Button>
          </div>
        </>
      )}

      {step === "mfa" && mfaOptions && (
        <>
          <p className="text-sm text-gray-500">
            {t("carrier.fedexCsp.mfaDescription")}
          </p>
          {mfaOptions.email && (
            <p className="text-sm text-gray-600">
              {t("carrier.fedexCsp.email")}: {mfaOptions.email}
            </p>
          )}
          {mfaOptions.phoneNumber && (
            <p className="text-sm text-gray-600">
              {t("carrier.fedexCsp.phone")}: {mfaOptions.phoneNumber}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {mfaOptions.options?.secureCode?.includes("EMAIL") && (
              <Button
                variant="outline"
                disabled={isLoading}
                onClick={() => handleRequestPin("EMAIL")}
              >
                {isLoading ? "..." : t("carrier.fedexCsp.sendViaEmail")}
              </Button>
            )}
            {mfaOptions.options?.secureCode?.includes("SMS") && (
              <Button
                variant="outline"
                disabled={isLoading}
                onClick={() => handleRequestPin("SMS")}
              >
                {isLoading ? "..." : t("carrier.fedexCsp.sendViaSms")}
              </Button>
            )}
            {mfaOptions.options?.secureCode?.includes("CALL") && (
              <Button
                variant="outline"
                disabled={isLoading}
                onClick={() => handleRequestPin("CALL")}
              >
                {isLoading ? "..." : t("carrier.fedexCsp.callMe")}
              </Button>
            )}
            {mfaOptions?.options?.invoice && (
              <Button variant="outline" disabled={isLoading} onClick={() => setStep("invoice")}>
                {isLoading ? "..." : t("carrier.fedexCsp.verifyByInvoice")}
              </Button>
            )}
          </div>
        </>
      )}

      {step === "pin" && (
        <>
          <p className="text-sm text-gray-500">
            {t("carrier.fedexCsp.pinDescription")}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("carrier.fedexCsp.pinLabel")}
            </label>
            <Input
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder={t("carrier.fedexCsp.pinPlaceholder")}
              className="mt-1"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="default"
              size="lg"
              disabled={isLoading || !pin}
              onClick={handleValidatePin}
            >
              {isLoading
                ? t("carrier.fedexCsp.validating")
                : t("carrier.fedexCsp.validatePin")}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => setStep("mfa")}
            >
              {t("carrier.fedexCsp.back")}
            </Button>
          </div>
        </>
      )}

      {step === "invoice" && (
        <>
          <p className="text-sm text-gray-500">{t("carrier.fedexCsp.invoiceDescription")}</p>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("carrier.fedexCsp.invoiceNumber")}</label>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value.replace(/\D/g, "").slice(0, 9))}
              inputMode="numeric"
              maxLength={9}
              placeholder="234562278"
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("carrier.fedexCsp.invoiceCurrency")}</label>
              <Input value={invoiceCurrency} onChange={(e) => setInvoiceCurrency(e.target.value)} placeholder="USD" className="mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("carrier.fedexCsp.invoiceDate")}</label>
              <Input value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} placeholder="YYYY-MM-DD" className="mt-1" />
              <p className="text-xs text-gray-500 mt-1">{t("carrier.fedexCsp.invoiceDateHint")}</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("carrier.fedexCsp.invoiceAmount")}</label>
            <Input value={invoiceAmount} onChange={(e) => setInvoiceAmount(e.target.value)} placeholder="234.00" className="mt-1" />
          </div>
          <div className="flex gap-2">
            <Button variant="default" size="lg" disabled={isLoading || !invoiceNumber || !invoiceDate || !invoiceAmount} onClick={handleValidateInvoice}>
              {isLoading ? t("carrier.fedexCsp.validating") : t("carrier.fedexCsp.validateInvoice")}
            </Button>
            <Button variant="outline" size="lg" onClick={() => setStep("mfa")}>
              {t("carrier.fedexCsp.back")}
            </Button>
          </div>
        </>
      )}
      <p className="text-[11px] text-gray-500 mt-2">
        {t("carrier.fedexCsp.supportingServices")}
      </p>
      <p className="text-[10px] text-gray-400">
        {t("carrier.fedexCsp.disclaimer")}
      </p>
    </div>
  );
}
